import { CommerceRepository } from "../repository";
import { Article, ArticleFilter, Cart, Collection, PagedResult, Product, ProductFilter, ProductSortKey } from "../types";
import { assertNoUserErrors, shopifyFetch, ShopifyApiError, UserError } from "./client";
import {
    BLOGS_WITH_ARTICLES_QUERY,
    CART_CREATE_MUTATION,
    CART_LINES_ADD_MUTATION,
    CART_LINES_REMOVE_MUTATION,
    CART_LINES_UPDATE_MUTATION,
    CART_QUERY,
    COLLECTIONS_QUERY,
    COLLECTION_BY_HANDLE_QUERY,
    COLLECTION_PRODUCTS_QUERY,
    PRODUCTS_QUERY,
    PRODUCT_BY_HANDLE_QUERY,
    PRODUCT_RECOMMENDATIONS_QUERY,
    PRODUCT_TYPES_QUERY,
} from "./queries";
import { Connection, fromEdges, mapArticle, mapCart, mapCollection, mapProduct, ShopifyArticle, ShopifyCart, ShopifyCollection, ShopifyProduct } from "./mappers";

function quoteSearchValue(value: string): string {
    return `'${value.replace(/'/g, "\\'")}'`;
}

function buildSearchQuery(filter?: ProductFilter): string | undefined {
    const clauses: string[] = [];
    if (filter?.category) clauses.push(`tag:${quoteSearchValue(filter.category)}`);
    if (filter?.productType) clauses.push(`product_type:${quoteSearchValue(filter.productType)}`);
    if (filter?.query) clauses.push(`title:*${filter.query}*`);
    return clauses.length > 0 ? clauses.join(" AND ") : undefined;
}

function mapSortKey(sortKey?: ProductSortKey): { sortKey: string; reverse: boolean } {
    switch (sortKey) {
        case "PRICE_ASC":
            return { sortKey: "PRICE", reverse: false };
        case "PRICE_DESC":
            return { sortKey: "PRICE", reverse: true };
        case "TITLE":
            return { sortKey: "TITLE", reverse: false };
        case "CREATED":
        default:
            return { sortKey: "CREATED_AT", reverse: true };
    }
}

function mapCollectionSortKey(sortKey?: ProductSortKey): { sortKey: string; reverse: boolean } {
    const mapped = mapSortKey(sortKey);
    return { sortKey: mapped.sortKey === "CREATED_AT" ? "CREATED" : mapped.sortKey, reverse: mapped.reverse };
}

async function getProducts(filter?: ProductFilter): Promise<PagedResult<Product>> {
    const needsPriceFilter = filter?.priceMin !== undefined || filter?.priceMax !== undefined;
    const requestedFirst = filter?.first ?? 100;
    const fetchFirst = needsPriceFilter ? Math.min(Math.max(requestedFirst * 3, 100), 250) : requestedFirst;

    let rawProducts: ShopifyProduct[];
    let hasNextPage: boolean;

    if (filter?.collectionHandle) {
        const { sortKey, reverse } = mapCollectionSortKey(filter.sortKey);
        const data = await shopifyFetch<{
            collection: { products: Connection<ShopifyProduct> & { pageInfo: { hasNextPage: boolean } } } | null;
        }>(COLLECTION_PRODUCTS_QUERY, { handle: filter.collectionHandle, first: fetchFirst, sortKey, reverse });
        rawProducts = fromEdges(data.collection?.products);
        hasNextPage = data.collection?.products.pageInfo.hasNextPage ?? false;
    } else {
        const { sortKey, reverse } = mapSortKey(filter?.sortKey);
        const query = buildSearchQuery(filter);
        const data = await shopifyFetch<{
            products: Connection<ShopifyProduct> & { pageInfo: { hasNextPage: boolean } };
        }>(PRODUCTS_QUERY, { first: fetchFirst, query, sortKey, reverse });
        rawProducts = fromEdges(data.products);
        hasNextPage = data.products.pageInfo.hasNextPage;
    }

    let items = rawProducts.map(mapProduct);
    if (filter?.priceMin !== undefined) {
        items = items.filter((p) => parseFloat(p.priceRange.minVariantPrice.amount) >= filter.priceMin!);
    }
    if (filter?.priceMax !== undefined) {
        items = items.filter((p) => parseFloat(p.priceRange.minVariantPrice.amount) <= filter.priceMax!);
    }
    const truncated = items.length > requestedFirst;
    items = items.slice(0, requestedFirst);

    return {
        items,
        hasNextPage: needsPriceFilter ? truncated : hasNextPage,
        endCursor: items.length > 0 ? items[items.length - 1].id : null,
    };
}

async function getProductByHandle(handle: string): Promise<Product | null> {
    const data = await shopifyFetch<{ product: ShopifyProduct | null }>(PRODUCT_BY_HANDLE_QUERY, { handle });
    return data.product ? mapProduct(data.product) : null;
}

async function getRelatedProducts(handle: string, first = 4): Promise<Product[]> {
    const product = await getProductByHandle(handle);
    if (!product) return [];
    const data = await shopifyFetch<{ productRecommendations: ShopifyProduct[] | null }>(PRODUCT_RECOMMENDATIONS_QUERY, {
        productId: product.id,
    });
    return (data.productRecommendations ?? []).map(mapProduct).slice(0, first);
}

async function getProductTypes(category?: string): Promise<string[]> {
    const query = category ? `tag:${quoteSearchValue(category)}` : undefined;
    const data = await shopifyFetch<{ products: Connection<{ productType: string }> }>(PRODUCT_TYPES_QUERY, { query });
    const types = fromEdges(data.products)
        .map((n) => n.productType)
        .filter(Boolean);
    return Array.from(new Set(types));
}

async function getCollections(): Promise<Collection[]> {
    const data = await shopifyFetch<{ collections: Connection<ShopifyCollection> }>(COLLECTIONS_QUERY);
    return fromEdges(data.collections).map(mapCollection);
}

async function getCollectionByHandle(handle: string): Promise<Collection | null> {
    const data = await shopifyFetch<{ collection: ShopifyCollection | null }>(COLLECTION_BY_HANDLE_QUERY, { handle });
    return data.collection ? mapCollection(data.collection) : null;
}

let articlesCache: Article[] | null = null;

/**
 * The Storefront API scopes articles under a blog, with no global "all articles"
 * query, so every article-facing method fetches once (per session) across all
 * blogs and filters/sorts client-side, mirroring the mock repository's approach.
 */
async function getAllArticles(): Promise<Article[]> {
    if (articlesCache) return articlesCache;
    const data = await shopifyFetch<{ blogs: Connection<{ handle: string; articles: Connection<ShopifyArticle> }> }>(
        BLOGS_WITH_ARTICLES_QUERY,
        { articlesFirst: 250 }
    );
    const blogs = fromEdges(data.blogs);
    articlesCache = blogs.flatMap((b) => fromEdges(b.articles)).map(mapArticle);
    return articlesCache;
}

function matchesArticleFilter(article: Article, filter?: ArticleFilter): boolean {
    if (!filter) return true;
    if (filter.category && !article.tags.includes(filter.category)) return false;
    if (filter.query) {
        const q = filter.query.toLowerCase();
        if (!article.title.toLowerCase().includes(q) && !article.excerpt.toLowerCase().includes(q)) return false;
    }
    return true;
}

async function getArticles(filter?: ArticleFilter): Promise<PagedResult<Article>> {
    const all = await getAllArticles();
    const sorted = all
        .filter((a) => matchesArticleFilter(a, filter))
        .sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime());
    const first = filter?.first ?? sorted.length;
    const items = sorted.slice(0, first);
    return {
        items,
        hasNextPage: first < sorted.length,
        endCursor: items.length > 0 ? items[items.length - 1].id : null,
    };
}

async function getArticleByHandle(handle: string): Promise<Article | null> {
    const all = await getAllArticles();
    return all.find((a) => a.handle === handle) ?? null;
}

async function getArticleCategories(): Promise<string[]> {
    const all = await getAllArticles();
    return Array.from(new Set(all.flatMap((a) => a.tags)));
}

async function getRelatedArticles(handle: string, first = 3): Promise<Article[]> {
    const all = await getAllArticles();
    const article = all.find((a) => a.handle === handle);
    if (!article) return [];
    return all.filter((a) => a.handle !== handle && a.tags.some((t) => article.tags.includes(t))).slice(0, first);
}

async function createCart(): Promise<Cart> {
    const data = await shopifyFetch<{ cartCreate: { cart: ShopifyCart | null; userErrors: UserError[] } }>(CART_CREATE_MUTATION);
    assertNoUserErrors(data.cartCreate.userErrors, "Warenkorb konnte nicht erstellt werden");
    if (!data.cartCreate.cart) throw new ShopifyApiError("Warenkorb konnte nicht erstellt werden");
    return mapCart(data.cartCreate.cart);
}

async function getCart(cartId: string): Promise<Cart | null> {
    const data = await shopifyFetch<{ cart: ShopifyCart | null }>(CART_QUERY, { id: cartId });
    return data.cart ? mapCart(data.cart) : null;
}

async function addCartLine(cartId: string, variantId: string, quantity: number): Promise<Cart> {
    const data = await shopifyFetch<{ cartLinesAdd: { cart: ShopifyCart | null; userErrors: UserError[] } }>(
        CART_LINES_ADD_MUTATION,
        { cartId, lines: [{ merchandiseId: variantId, quantity }] }
    );
    assertNoUserErrors(data.cartLinesAdd.userErrors, "Artikel konnte nicht hinzugefügt werden");
    if (!data.cartLinesAdd.cart) throw new ShopifyApiError("Warenkorb nicht gefunden");
    return mapCart(data.cartLinesAdd.cart);
}

async function removeCartLine(cartId: string, lineId: string): Promise<Cart> {
    const data = await shopifyFetch<{ cartLinesRemove: { cart: ShopifyCart | null; userErrors: UserError[] } }>(
        CART_LINES_REMOVE_MUTATION,
        { cartId, lineIds: [lineId] }
    );
    assertNoUserErrors(data.cartLinesRemove.userErrors, "Artikel konnte nicht entfernt werden");
    if (!data.cartLinesRemove.cart) throw new ShopifyApiError("Warenkorb nicht gefunden");
    return mapCart(data.cartLinesRemove.cart);
}

async function updateCartLine(cartId: string, lineId: string, quantity: number): Promise<Cart> {
    if (quantity <= 0) {
        return removeCartLine(cartId, lineId);
    }
    const data = await shopifyFetch<{ cartLinesUpdate: { cart: ShopifyCart | null; userErrors: UserError[] } }>(
        CART_LINES_UPDATE_MUTATION,
        { cartId, lines: [{ id: lineId, quantity }] }
    );
    assertNoUserErrors(data.cartLinesUpdate.userErrors, "Menge konnte nicht aktualisiert werden");
    if (!data.cartLinesUpdate.cart) throw new ShopifyApiError("Warenkorb nicht gefunden");
    return mapCart(data.cartLinesUpdate.cart);
}

export const shopifyRepository: CommerceRepository = {
    getProducts,
    getProductByHandle,
    getRelatedProducts,
    getProductTypes,
    getCollections,
    getCollectionByHandle,
    getArticles,
    getArticleByHandle,
    getArticleCategories,
    getRelatedArticles,
    createCart,
    getCart,
    addCartLine,
    updateCartLine,
    removeCartLine,
};
